import _, {difference, findLast, groupBy, keyBy, noop} from 'lodash';

import {UnreachableError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {delayAsync} from '../../../../helpers/asyncHelpers';
import {
  conversationsTrackerBumpedAt,
  findConversationsReminder,
  findConversationStatus,
  findTracker,
  findTrackerStatus,
  makeFindConversationActionContext,
} from '../../../../helpers/models/conversationHelpers';
import {ConversationId} from '../../../../models/api/conversationCommonTypes';
import {ConversationTrackerStatusesEnum} from '../../../../models/api/conversationTrackerModel';
import {Dispatch} from '../../../appStore';
import {ThunkAction, ThunkAsyncAction} from '../../../appThunk';
import {assignToTeammateAction} from '../../../data/conversations/assigneeConversationsActions';
import {setConversationsReminderAction} from '../../../data/conversations/reminderConversationsActions';
import {setConversationsStatusAction} from '../../../data/conversations/statusConversationsActions';
import {subscribeToConversationsAction} from '../../../data/conversations/subscribeConversationsActions';
import {addAndRemoveTagsFromConversationsAction} from '../../../data/conversations/tagConversationsActions';
import {cancelMessageAsyncAction} from '../../../data/messages/cancelMessageAction';
import {navigateToConversation} from '../../../stateless/navigationActions';
import {AppPanelId} from '../../panelStateBase';
import {undoAccountDetailsBannerAction} from './accountDetailsUndoBannersActions';
import {removeBannerAction} from './bannersActions';
import {makeGetUndoBannersForPanel} from './bannersSelectors';
import {
  ConversationUndoBannerState,
  ConversationUndoBannerTypesEnum,
  MessageUndoBannerState,
  UndoBannerState,
  UndoBannerTypesEnum,
} from './bannersState';
import {undoContactDetailsBannerAction} from './contactDetailsUndoBannersActions';

/*
 * Generic.
 */

export function undoLastBannerAsyncAction(panelId: AppPanelId): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Find a banner to undo.
    const undoBanners = makeGetUndoBannersForPanel()(getState(), {panelId});
    const undoBanner = findLast(undoBanners, canUndoBanner);

    // If no banner was found to undo, nothing to do.
    if (!undoBanner) {
      return;
    }

    // Otherwise, undo it.
    switch (undoBanner.undoType) {
      case UndoBannerTypesEnum.MESSAGE:
        await dispatch(undoMessageBannerAsyncAction(panelId, undoBanner));
        return;

      case UndoBannerTypesEnum.CONVERSATION:
        await dispatch(undoConversationBannerAsyncAction(panelId, undoBanner));
        break;

      case UndoBannerTypesEnum.CONTACT_DETAILS:
        await dispatch(undoContactDetailsBannerAction(panelId, undoBanner));
        return;

      case UndoBannerTypesEnum.ACCOUNT_DETAILS:
        await dispatch(undoAccountDetailsBannerAction(panelId, undoBanner));
        return;

      default:
        throw new UnreachableError(undoBanner);
    }
  };
}

function canUndoBanner(banner: UndoBannerState): boolean {
  // For message banners, make sure the banner is not expired.
  if (banner.undoType === UndoBannerTypesEnum.MESSAGE) {
    return banner.expiresAt > Date.now();
  }

  // All other banners can always be undone.
  return true;
}

/*
 * Message.
 */

export function undoMessageBannerAsyncAction(
  panelId: AppPanelId,
  banner: MessageUndoBannerState,
): ThunkAsyncAction {
  return async (dispatch) => {
    // Perform the undo.
    const undoPromise = delayAsync().then(() => dispatch(cancelMessageAsyncAction(banner.messageUid)));

    // Hide the banner from the UI.
    dispatch(removeBannerAction(panelId, banner.uid));

    await undoPromise;
  };
}

/*
 * Conversation.
 */

export function viewConversationBannerAction(banner: ConversationUndoBannerState): ThunkAction {
  return (dispatch) => {
    if (!banner.newConversations[0]) {
      return;
    }

    dispatch(navigateToConversation(banner.newConversations[0].id));
  };
}

export function undoConversationBannerAsyncAction(
  panelId: AppPanelId,
  banner: ConversationUndoBannerState,
): ThunkAsyncAction {
  return async (dispatch) => {
    // Perform the undo.
    const undoPromise = delayAsync(300).then(() => undoConversationBannerAsync(dispatch, banner));

    // Hide the banner from the UI.
    dispatch(removeBannerAction(panelId, banner.uid));

    await undoPromise;
  };
}

async function undoConversationBannerAsync(dispatch: Dispatch, banner: ConversationUndoBannerState) {
  switch (banner.conversationUndoType) {
    case ConversationUndoBannerTypesEnum.ASSIGNEE:
      return undoAssigneeAsync(dispatch, banner);

    case ConversationUndoBannerTypesEnum.REMINDER:
      return undoReminderAsync(dispatch, banner);

    case ConversationUndoBannerTypesEnum.STAGE:
      return undoStageAsync(dispatch, banner);

    case ConversationUndoBannerTypesEnum.STATUS:
      return undoStatusAsync(dispatch, banner);

    // Doesn't actually allow moving back
    case ConversationUndoBannerTypesEnum.MOVE: {
      return noop;
    }

    case ConversationUndoBannerTypesEnum.TAG:
    default:
      return undoTagAsync(dispatch, banner);
  }
}

async function undoAssigneeAsync(
  dispatch: Dispatch,
  {oldConversations, cellsContext}: ConversationUndoBannerState,
) {
  const {teammateId} = cellsContext;

  // Group the old conversations by their assignee and tracker status.
  const conversationGroups = groupBy(
    oldConversations,
    (c) => `${c.assigneeId}-${findTrackerStatus(c, teammateId)}`,
  );

  // For each group, queue a batch update.
  await Promise.all(
    Object.values(conversationGroups).map(async (conversations) => {
      const conversationIds = conversations.map((c) => c.id);

      // Find the previous assignee.
      const {assigneeId} = conversations[0];

      // Find the previous tracker status.
      // TODO: Should/can we undo the bumped date?
      const trackerStatus = findTrackerStatus(conversations[0], teammateId);
      const shouldUnarchive = trackerStatus === ConversationTrackerStatusesEnum.INBOX;

      await dispatch(
        assignToTeammateAction(conversationIds, assigneeId, {
          cellsContext,
          preventUndo: true,
          shouldUnarchive,
        }),
      );
    }),
  );
}

async function undoReminderAsync(
  dispatch: Dispatch,
  {oldConversations, cellsContext}: ConversationUndoBannerState,
) {
  const conversationIds = oldConversations.map((c) => c.id);
  const remindAt = findConversationsReminder(cellsContext, oldConversations)?.remindAt || null;
  const previousBumpedAt = conversationsTrackerBumpedAt(oldConversations, cellsContext.teammateId);
  const findConversationActionContext = makeFindConversationActionContext(cellsContext, oldConversations);

  await dispatch(
    setConversationsReminderAction(cellsContext, conversationIds, remindAt, findConversationActionContext, {
      previousBumpedAt,
      preventUndo: true,
    }),
  );
}

async function undoStageAsync(
  dispatch: Dispatch,
  {oldConversations, cellsContext}: ConversationUndoBannerState,
) {
  // Group the old conversations by their tracker status.
  const {teammateId} = cellsContext;
  const conversationGroups = groupBy(oldConversations, (c) => findTracker(c, teammateId).status);

  // For each group, revert subscribe again.
  await Promise.all(
    Object.values(conversationGroups).map(async (conversations) => {
      const previousStatus = findTracker(conversations[0], teammateId).status;
      const conversationIds = conversations.map((c) => c.id);
      const previousBumpedAt = conversationsTrackerBumpedAt(conversations, teammateId);

      await dispatch(
        subscribeToConversationsAction(cellsContext, conversationIds, {previousStatus, previousBumpedAt}),
      );
    }),
  );
}

async function undoStatusAsync(
  dispatch: Dispatch,
  {oldConversations, cellsContext}: ConversationUndoBannerState,
) {
  // Group the old conversations by their context status.
  const conversationGroups = groupBy(oldConversations, (c) => findConversationStatus(cellsContext, c));

  // For each group, queue a batch update.
  await Promise.all(
    Object.values(conversationGroups).map(async (conversations) => {
      const conversationIds = conversations.map((c) => c.id);
      const status = findConversationStatus(cellsContext, conversations[0]);
      const conversationIdToBumpedAt = conversationsTrackerBumpedAt(conversations, cellsContext.teammateId);
      const findBumpedAt = (conversationId: ConversationId) => conversationIdToBumpedAt[conversationId];
      const findConversationActionContext = makeFindConversationActionContext(cellsContext, conversations);

      await dispatch(
        setConversationsStatusAction(cellsContext, conversationIds, status, findConversationActionContext, {
          preventUndo: true,
          findBumpedAt,
        }),
      );
    }),
  );
}

async function undoTagAsync(
  dispatch: Dispatch,
  {newConversations, oldConversations}: ConversationUndoBannerState,
) {
  // Group the old conversations by what tags were added/removed.
  const newConversationsMap = keyBy(newConversations, (c) => c.id);
  const conversationGroups = groupBy(oldConversations, (oldConversation) => {
    const newConversation = newConversationsMap[oldConversation.id];
    const newTagIds = newConversation && newConversation.tagIds;

    const addedTagIds = difference(newTagIds, oldConversation.tagIds);
    const removedTagIds = difference(oldConversation.tagIds, newTagIds);

    return _(addedTagIds).concat(removedTagIds).sortBy().value();
  });

  // For each group, queue a batch update.
  await Promise.all(
    Object.values(conversationGroups).map(async (conversations) => {
      const oldConversation = conversations[0];
      const newConversation = newConversationsMap[oldConversation.id];
      if (!newConversation) {
        return;
      }

      const conversationIds = conversations.map((c) => c.id);
      const updateTagOptions = {
        add: difference(oldConversation.tagIds, newConversation.tagIds),
        remove: difference(newConversation.tagIds, oldConversation.tagIds),
      };

      await dispatch(addAndRemoveTagsFromConversationsAction(conversationIds, updateTagOptions));
    }),
  );
}
